import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter } from "react-router-dom";
import "./index.css";
import App from "./App.tsx";
import { store, persistor } from "@/state/store.ts";
import { LocationProvider } from "@/contexts/LocationContext";
import { Amplify } from "aws-amplify";
import ENV from "@/lib/env";
import MindWiseService from "@/services/MindWiseService";

// Expose MindWiseService for developer testing in console
if (typeof window !== "undefined") {
  (window as any).MindWiseService = MindWiseService;
}

// Automatically reload page when a new deployment invalidates dynamic chunk hashes
window.addEventListener('vite:preloadError', (event) => {
  console.warn('Deployment update detected (dynamic chunk missing). Reloading page...', event);
  window.location.reload();
});

// Base Cognito Configuration
const cognitoConfig: any = {
  userPoolId: ENV.VITE_AWS_USER_POOL_ID,
  userPoolClientId: ENV.VITE_AWS_APP_CLIENT_ID,
  loginWith: {
    email: true,
  },
  signUpVerificationMethod: "code",
  userAttributes: {
    email: {
      required: true,
    },
  },
  passwordFormat: {
    minLength: 8,
    requireLowercase: true,
    requireUppercase: true,
    requireNumbers: true,
    requireSpecialCharacters: true,
  },
};

// Only add Identity Pool config if it's set and not a placeholder
const identityPoolId = ENV.VITE_AWS_IDENTITY_POOL_ID;
if (identityPoolId && !identityPoolId.includes('placeholder')) {
  cognitoConfig.identityPoolId = identityPoolId;
  cognitoConfig.allowGuestAccess = true;
}

Amplify.configure({
  Auth: {
    Cognito: cognitoConfig,
  },
  Storage: {
    S3: {
      bucket: ENV.VITE_AWS_S3_BUCKET,
      region: ENV.VITE_AWS_S3_REGION,
    },
  },
  API: {
    REST: {
      // API Gateway URL for Lambda functions (DB, Email, etc.)
      MonitorHealthAPI: {
        endpoint: ENV.VITE_AWS_API_GATEWAY_URL,
        region: ENV.VITE_AWS_REGION,
      },
    },
  },
});

// // Force logout on start (uncomment to use)
// const forceLogout = async () => {
//   try {
//     console.log("🔄 Force logout triggered...");
//     await signOut();
//     store.dispatch(logOut());
//     console.log("✅ Force logout complete");
//   } catch (err) {
//     console.error("❌ Force logout failed:", err);
//   }
// };
// await forceLogout();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <LocationProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </LocationProvider>
      </PersistGate>
    </Provider>
  </StrictMode>,
);
